import React, { useState, useEffect } from "react";
import { withTranslation } from "react-i18next";
import { connect, useSelector } from "react-redux";
import { createStructuredSelector } from "reselect";
import { Form, Checkbox, Button, Space, Typography, Row, Col } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import moment from "moment";

import { EditableTable } from "./EditableTable";
import { DueDateFormItem } from "./form/DueDateFormItem";
import { TaskFormItem } from "./form/TaskFormItem";
import { AssigneeFormItem } from "./form/AssigneeFormItem";
import ACTIONS from "../store/action";
import { selectIsFetching, selectPatientTasks, selectAssigneesAndUserGroups, selectUser } from "../store/selector";
import { ApiNames } from "../api/api";
import { dateSort, stringSort } from "../utils/sort";
import { SORT_DIRECTIONS, ASCEND } from "../constant/sort";
import { DateRangeFilter } from "./filter/DateRangeFilter";
import { AssigneeFilter } from "./filter/AssigneeFilter";
import { TaskStatusFilter } from "./filter/TaskStatusFilter";
import { ALL_ASSIGNEES, defaultTaskFilters, UNASSIGNED } from "../constant/filters";
import { inRange } from "../utils/date";
import { formatAssignee } from "../utils/formaters";
import { ALL } from "../constant/filters";
import "./PatientTaskTab.css";
import { ReactComponent as DeleteIcon } from "../assets/svg/delete-new-icon.svg";
import { ModalComponent } from "../component/customComponent/ModalComponent";

const { Text } = Typography;

const ReachableContext = React.createContext();

const PHARMACY_PATIENT_TASKS_FILTER = "pharmacyPatientTasksFilter";
const tableId = "tasks-table";

const PatientTaskTabComponent = ({
  t,
  patient,
  isActive,
  setEdited,
  patientTasks,
  assignees,
  isFetching,
  addPatientTaskAct,
  editPatientTaskAct,
  deletePatientTaskAct,
  onFilteredCountChange
}) => {
  const saveFilteredTasksToLocalStorage = (filters) => {
    localStorage.setItem(PHARMACY_PATIENT_TASKS_FILTER, JSON.stringify(filters));
  };

  const getFilteredTasksFromLocalStorage = () => {
    const filters = localStorage.getItem(PHARMACY_PATIENT_TASKS_FILTER);

    if (!filters) {
      localStorage.setItem(PHARMACY_PATIENT_TASKS_FILTER, JSON.stringify(defaultTaskFilters));
      return defaultTaskFilters;
    }

    return JSON.parse(filters);
  };

  const user = useSelector(selectUser);

  const [filteredTasks, setFilteredTasks] = useState([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [ChangingStatusRowKey, setChangingStatusRowKey] = useState(null);
  const [form] = Form.useForm();
  const [selectedFilters, setSelectedFilters] = useState(getFilteredTasksFromLocalStorage());
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState({});

  const { assigneesFilter, timeRange, status } = selectedFilters;

  const assigneesForFilter = Object.keys(assignees).map((key) => ({
    text: formatAssignee(assignees[key]),
    value: assignees[key].id
  }));

  useEffect(() => {
    if (assignees.length === 0 || !user) return;

    const currentAssigneeUser = assignees.find((a) => a.id === user?.id)?.id;

    const initialAssignee = selectedFilters.isAssigneesFilterModified
      ? selectedFilters.assigneesFilter
      : selectedFilters.assigneesFilter.length > 0
      ? selectedFilters.assigneesFilter
      : currentAssigneeUser
      ? [currentAssigneeUser]
      : [ALL_ASSIGNEES];

    setSelectedFilters({ ...selectedFilters, assigneesFilter: initialAssignee });
  }, [assignees, user]);

  useEffect(() => {
    if (selectedFilters) saveFilteredTasksToLocalStorage(selectedFilters);
  }, [selectedFilters]);

  useEffect(() => {
    const filteredTasks = applyFilters(patientTasks, selectedFilters, patient);
    setFilteredTasks(filteredTasks);
    if (onFilteredCountChange) {
      onFilteredCountChange(filteredTasks.length);
    }
  }, [patientTasks, selectedFilters, patient, onFilteredCountChange]);

  const columns = [
    {
      title: t("patient.tasks.col_title_status"),
      dataIndex: "status",
      width: 80,
      align: "center",
      render: (text, record) => (
        <Checkbox
          className="custom-checkbox"
          checked={
            (ChangingStatusRowKey !== record.key && record.status) ||
            (ChangingStatusRowKey === record.key && !record.status)
          }
        ></Checkbox>
      ),
      onCell: (record) => ({
        id: `tt-status-checkbox-${record.key}`,
        onClick: () => {
          if (!ChangingStatusRowKey) {
            record.status = record.status ? null : "checked";
            setChangingStatusRowKey(record.key);
            setTimeout(function () {
              onEdit(record);
              setChangingStatusRowKey(null);
            }, 1500);
          }
        }
      }),
      editable: true,
      inputNode: () => {
        return <Checkbox disabled={true} style={{ marginBottom: 0 }}></Checkbox>;
      }
    },
    {
      title: t("patient.tasks.col_title_due_date"),
      dataIndex: "dueDate",
      width: 130,
      render: (text, record) => {
        const dueDate = moment(record.dueDate);
        if (!record.dueDate) {
          return "";
        }

        if (dueDate < moment().subtract(1, "days")) {
          return <Text style={{ paddingLeft: 6, color: "#CA367E" }}>{dueDate.format("MMM D, YYYY")}</Text>;
        } else {
          return dueDate.format("MMM D, YYYY");
        }
      },
      sortDirections: SORT_DIRECTIONS,
      sorter: (a, b, sortDirection) => dateSort(a.dueDate, b.dueDate, sortDirection),
      defaultSortOrder: ASCEND,
      editable: true,
      inputNode: () => {
        return <DueDateFormItem required={false} style={{ marginBottom: 0 }} />;
      }
    },
    {
      title: t("patient.tasks.col_title_assignee"),
      dataIndex: "assignee",
      width: 200,
      render: (text, record) => {
        const assignee = assignees.find((a) => String(a.id) === String(record.assignee));
        return record.assignee && assignee
          ? formatAssignee(assignee)
          : assignees[record.assignee]
          ? formatAssignee(assignees[record.assignee])
          : "";
      },
      sortDirections: SORT_DIRECTIONS,
      sorter: (a, b, sortDirection) => {
        const aAssignee = assignees.find((assignee) => String(assignee.id) === String(a.assignee));
        const bAssignee = assignees.find((assignee) => String(assignee.id) === String(b.assignee));

        const aAssigneeName =
          a.assignee && aAssignee
            ? formatAssignee(aAssignee)
            : assignees[a.assignee]
            ? formatAssignee(assignees[a.assignee])
            : null;

        const bAssigneeName =
          b.assignee && bAssignee
            ? formatAssignee(bAssignee)
            : assignees[b.assignee]
            ? formatAssignee(assignees[b.assignee])
            : null;

        return stringSort(aAssigneeName, bAssigneeName, sortDirection);
      },
      editable: true,
      inputNode: () => {
        return <AssigneeFormItem required={false} style={{ marginBottom: 0 }} />;
      },
      onCell: () => ({
        id: "patient_assignee_task"
      })
    },
    {
      title: t("patient.tasks.col_title_task"),
      dataIndex: "text",
      editable: true,
      inputNode: () => {
        return <TaskFormItem required={true} style={{ marginBottom: 0 }} />;
      },
      onCell: () => ({
        id: "patient_task"
      })
    }
  ];

  const onSubmit = (record) => {
    record.updatedBy = user.id;
    record.createdBy = user.id;
    record.patient = patient.patient.id;
    record.assignee = record.assignee ? record.assignee : null;
    record.assigneeType = assignees?.find((a) => String(a.id) === String(record?.assignee))?.assigneeType || 0;
    record.dueDate = record.dueDate ? record.dueDate : null;
    record.linkedPotentialSaving = patient.potentialSaving?.id;
    addPatientTaskAct(record, patient.id);
    form.resetFields();
  };

  const onEdit = (record) => {
    record.updatedBy = user.id;
    record.patient = patient.patient.id;
    record.updatedAt = moment().format();
    if (!record.linkedPotentialSaving) {
      record.linkedPotentialSaving = patient.potentialSaving?.id;
    }
    record.assigneeType = assignees?.find((a) => String(a.id) === String(record?.assignee))?.assigneeType || 0;
    editPatientTaskAct(record, patient.id);
  };

  const onDelete = (record) => {
    record.patient = patient.patient.id;
    record.updatedBy = user.id;
    deletePatientTaskAct(record, patient.id);
  };
  const tableDelete = (record) => {
    setSelectedRow(record);
    setShowDeleteModal(true);
  };
  const tableEdit = (values) => {
    values.assignee = values.assignee ? values.assignee : null;
    onEdit(values);
  };

  return (
    <ReachableContext.Provider value={null}>
      <Space direction={"vertical"} size={"large"} style={{ width: "87vw", marginBottom: 20 }}>
        {isFormOpen ? (
          <Form
            name="newTask"
            form={form}
            onFinish={(values) => onSubmit(values)}
            initialValues={{
              assignee: assignees.find((a) => a.id === user?.id)?.id
            }}
          >
            <Row align="top" style={{ width: "100%", backgroundColor: "#F9FBFF", padding: 16 }}>
              <Col span={12} style={{ paddingRight: 16 }}>
                <TaskFormItem textArea={false} required={true} style={{ marginBottom: 0 }} id="patient_task_input" />
              </Col>
              <Col span={4}>
                <AssigneeFormItem style={{ marginBottom: 0, paddingRight: 6 }} required={false} />
              </Col>
              <Col span={4}>
                <DueDateFormItem style={{ marginBottom: 0, paddingRight: 6 }} required={false} />
              </Col>
            </Row>
            <Row
              justify="end"
              style={{
                width: "100%",
                backgroundColor: "#F9FBFF",
                padding: "0px 16px 0px 16px"
              }}
            >
              <Form.Item>
                <Button
                  type="text"
                  id="tt-add-task-reset"
                  onClick={() => {
                    form.resetFields();
                    setIsFormOpen(false);
                  }}
                >
                  <Text style={{ color: "#2B56A3" }}>{t("cancel")}</Text>
                </Button>
                <Button
                  data-tm-id="tt-add-task-btn"
                  id="tt-add-task-submit"
                  htmlType="submit"
                  loading={isFetching}
                  type="primary"
                >
                  {t("patient.tasks.add_button")}
                </Button>
              </Form.Item>
            </Row>
          </Form>
        ) : (
          <Button
            data-tm-id="tt-add-task-btn"
            id="tt-add-task-btn"
            icon={<PlusOutlined style={{ fontSize: 12 }} />}
            onClick={() => setIsFormOpen(true)}
            type="primary"
          >
            {t("patient.tasks.add_button")}
          </Button>
        )}

        <Row gutter={[8, 8]}>
          <Col span={4}>
            <AssigneeFilter
              value={assigneesFilter.includes(ALL_ASSIGNEES) ? [] : assigneesFilter}
              onChange={(value) =>
                setSelectedFilters({ ...selectedFilters, assigneesFilter: value, isAssigneesFilterModified: true })
              }
              assignees={assigneesForFilter}
              dataTMId="tt-all-dropdown"
            />
          </Col>
          <Col span={4}>
            <TaskStatusFilter
              value={status}
              onSelect={(value) => setSelectedFilters({ ...selectedFilters, status: value })}
              dataTMId="tt-status-dropdown"
            />
          </Col>
          <Col span={4}>
            <DateRangeFilter
              timeRange={
                timeRange?.toDate
                  ? {
                      fromDate: moment(timeRange.fromDate),
                      toDate: moment(timeRange.toDate)
                    }
                  : timeRange
              }
              onSelect={(fromDate, toDate) =>
                setSelectedFilters({
                  ...selectedFilters,
                  timeRange: { fromDate, toDate }
                })
              }
              dataTMId="tt-all-times-dropdown"
            />
          </Col>
        </Row>
      </Space>
      <EditableTable
        id={tableId}
        name="pct-tt-tasks"
        t={t}
        isFormOpen={isFormOpen}
        columns={columns}
        setEdited={setEdited}
        isActive={isActive}
        data={filteredTasks.map((task, index) => ({
          ...task,
          key: index,
          dueDate: task.dueDate ? moment(task.dueDate) : null,
          assignee: task.assignee ? task.assignee : undefined
        }))}
        editAction={tableEdit}
        deleteAction={tableDelete}
      />
      <ModalComponent
        open={showDeleteModal}
        onCancel={() => {
          setShowDeleteModal(false);
        }}
        okText={t("ok_text")}
        onOk={async () => {
          onDelete(selectedRow);
          setShowDeleteModal(false);
        }}
      >
        <Space direction="vertical">
          <DeleteIcon style={{ width: "48px" }} />
          <Text className="title">{t("patient.tasks.confirm_delete_title")}</Text>
          <Text className="content">{t("patient.tasks.confirm_delete_content")}</Text>
        </Space>
      </ModalComponent>
    </ReachableContext.Provider>
  );
};

const applyFilters = (patientTasks, filters, patient, clearAll = false) => {
  const { status, assigneesFilter, timeRange } = filters;
  const currentPapId = patient?.potentialSaving?.id;

  return patientTasks.filter((task) => {
    const isStatusMatch = clearAll || status === ALL || task.status === status;

    const taskAssigneeStr = task.assignee != null ? String(task.assignee) : null;
    const isAssigneeMatch =
      clearAll ||
      assigneesFilter.length === 0 ||
      assigneesFilter.includes(ALL_ASSIGNEES) ||
      (assigneesFilter.includes(UNASSIGNED) && task.assignee === null) ||
      assigneesFilter.some((filterId) => String(filterId) === taskAssigneeStr);

    const isTimeRangeMatch = clearAll || timeRange === ALL || inRange(timeRange, task.dueDate);

    const isProgramMatch =
      clearAll || !currentPapId || !task.linkedPotentialSaving || currentPapId === task.linkedPotentialSaving;

    return isStatusMatch && isAssigneeMatch && isTimeRangeMatch && isProgramMatch;
  });
};

const mapStateToProps = createStructuredSelector({
  patientTasks: selectPatientTasks,
  assignees: selectAssigneesAndUserGroups,
  isFetching: selectIsFetching([ApiNames.TasksUpload, ApiNames.Task])
});

const mapDispatchToProps = (dispatch) => ({
  addPatientTaskAct: (task, pharmacyPatient) => dispatch(ACTIONS.addPatientTaskAction(task, pharmacyPatient)),
  editPatientTaskAct: (task, pharmacyPatient) => dispatch(ACTIONS.editPatientTaskAction(task, pharmacyPatient)),
  deletePatientTaskAct: (task, pharmacyPatient) => dispatch(ACTIONS.deletePatientTaskAction(task, pharmacyPatient))
});

const PatientTaskTab = connect(mapStateToProps, mapDispatchToProps)(withTranslation()(PatientTaskTabComponent));

export { PatientTaskTab };
